import { useSideMenuContext } from "$components/navigation";
import { createContext, useContext, useEffect } from "react";
import {
  type InventoryTableFilterState,
  useInventoryTableFilterState,
} from "./inventory-filter-state";
import {
  type IMoveDrawer,
  useMoveDrawerState,
} from "./move-drawer/use-move-drawer-state";
import { type MoveForm, useMoveForm } from "./move-drawer/use-move-form";
import {
  type IReceiveDrawer,
  useReceiveDrawerState,
} from "./receive-drawer/use-receive-drawer-state";

interface InventoryContextT {
  receiveDrawerState: IReceiveDrawer;
  moveDrawerState: IMoveDrawer;
  moveForm: MoveForm;
  inventoryTableFilterState: InventoryTableFilterState;
}

const InventoryContext = createContext<InventoryContextT | null>(null);

export const useReceiveDrawerContext = () =>
  useContext(InventoryContext)?.receiveDrawerState;

export const useMoveDrawerContext = () =>
  useContext(InventoryContext)?.moveDrawerState;

export const useMoveFormContext = () => useContext(InventoryContext)?.moveForm;

export const useInventoryTableFilterContext = () =>
  useContext(InventoryContext)?.inventoryTableFilterState;

export function InventoryContextProvider({ children }: WithChildren<unknown>) {
  const receiveDrawerState = useReceiveDrawerState();
  const moveDrawerState = useMoveDrawerState();
  const { closeMenu: closeGlobalSideMenu, isOpen: globalSideMenuOpen } =
    useSideMenuContext();

  useEffect(() => {
    if (globalSideMenuOpen) {
      receiveDrawerState.closeMenu();
      moveDrawerState.closeMenu();
    }
  }, [globalSideMenuOpen, moveDrawerState, receiveDrawerState]);

  const inventoryTableFilterState = useInventoryTableFilterState();
  const moveForm = useMoveForm();

  return (
    <InventoryContext.Provider
      value={{
        receiveDrawerState: {
          ...receiveDrawerState,
          openMenu: () => {
            closeGlobalSideMenu();
            moveDrawerState.closeMenu();
            receiveDrawerState.openMenu();
          },
        },
        moveDrawerState: {
          ...moveDrawerState,
          openMenu: () => {
            closeGlobalSideMenu();
            receiveDrawerState.closeMenu();
            moveDrawerState.openMenu();
          },
        },
        moveForm,
        inventoryTableFilterState,
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
}
